/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import React, { Component } from 'react';
// pieces
import Label from 'material/form/field/label/label';
import TextField from 'material/form/field/text/text';
import TextAreaField from 'material/form/field/textarea/textarea';
import SelectField from 'material/form/field/select/select';
import CheckboxField from 'material/form/field/checkbox/checkbox';
import RadioField from 'material/form/field/radio/radio';
import Info from 'material/form/field/info/info';
import ErrorMessage from 'material/form/field/errormessage/errormessage';
// utils
import { generateClasses } from 'utils/generators';
import { validation } from 'utils/validation';
// styles
import injectSheet from 'react-jss';
import styles from 'material/form/field/field_styles';

//READ BEFORE IMPLEMENTING THIS COMPONENT
//If your form has a discard state that leaves the user on the form page rather than navigating them to another page, you must
//  pass the formDisabled prop which contains the editable state of the form (to prevent error messaging during disabled state). 
//  For an example implementation, see: pages/dashboard/general/general.js.


/* ========== ~~~~~~~~~~ FORM : FIELD : CHILD ~~~~~~~~~~ ========== */
// PRESENTATIONAL COMPONENT
// receives app state & actions as props from parent "_container" component
// this child component handles local state and uses props passed from parent to read/write application state when necessary
class Field extends Component {
	constructor(props) {
		super(props);
		this.state = { 
			active: false, 
			valid: true,
			errorMessage: null, 
			hasValue: !!this.props.defaultValue
		};
	}
	componentDidUpdate(prevProps) {
        // CONTINUOUS VALIDATION OCCURS HERE
		if(this.props.value !== prevProps.value) this.triggerOnUpdateValidation(this.props.value)
	}

	//    ============-------============    EVENT HANDLERS     ============-------============    //

    handleBlur = (e) => {
        if (this.props.handleBlur) this.props.handleBlur(e);
		// VALIDATION OCCURS HERE
        this.triggerOnBlurValidation(e.target.value);
    };
	handleChange = (e) => {
		if (this.props.handleChange) this.props.handleChange(e);
		//IF TYPE IS OF CHECKBOX WE WILL SET VALUE TO EQUAL THE OPPOSITE OF ITS CURRENT CHECKED VALUE
		const value = this.props.type === 'checkbox' ? !this.props.checked : e.target.value;
        this.props.setFormFieldValue(this.props.formName, this.props.name, value);
	};
    handleFocus = (e) => {
        this.setState({ active: true });
        if (this.props.handleFocus) this.props.handleFocus(e);
    };
    handleKeyDown = (e) => {
		//ALWAYS VALIDATE FIELD ON ENTER CLICK, THEN CALL PARENT FUNCTION WITH EVENT AND VALID STATE
		if (e.key === 'Enter'){
			this.handleValidation(e.target.value);
		}
		if (this.props.handleKeyDown) this.props.handleKeyDown(e, this.isValid(e.target.value));
    };

    //    ============-------============    **************     ============-------============    //



    /////  ~~~~~~~~~~~ ---------- ~~~~~~~~~~~~  VALIDATION  ~~~~~~~~~~~ ---------- ~~~~~~~~~~~~  /////

	triggerOnUpdateValidation = fieldValue => {
        if (this.props.type === 'checkbox'||
        	this.props.type === 'radio'||
            this.props.type === 'password' ||
            this.props.type === 'text' ||
			this.props.type === 'select' ||
			this.props.validateOnChange ||
			this._shouldValidateOnSignIn(fieldValue)) this.handleValidation(fieldValue);
	};
	triggerOnBlurValidation = fieldValue => {
	
        this.handleValidation(fieldValue);
        this.setState({ active: false, hasValue: !!fieldValue });
	};
	handleValidation = fieldValue => {
		const validationStatus = this.isValid(fieldValue);
        if ((this.props.valid !== validationStatus) && !this.props.formDisabled) {this.props.setFieldValidation(this.props.formName, this.props.name, validationStatus) }
	};
	isValid = (fieldValue) => {
		const specificValidation = validation[this.props.name];
		let isValid = true;
		if (this.props.required) {
			switch (this.props.type) {
				case 'select':
					isValid = fieldValue !== '';
					break;
				case 'checkbox':
					isValid = true;
					break;
				case 'radio':
					isValid = true;
					break;
				default:
					isValid = this.specificValidationExists() ? specificValidation.test.test(fieldValue) : true;
					break;
			} 
		}
		return isValid;
	};
	getErrorMessage = () => {
		const specificValidation = validation[this.props.name];
		return this.specificValidationExists() ? specificValidation.message : validation['general'].message;
	}
	specificValidationExists = () => {
		return validation[this.props.name] !== undefined;
	};
    // Validate email:
	// - on change only if it vas previously invalidated. This will make UX a bit better when user corrects invalid email.
	// - on 3rd party password manager pre-filling credentials. This will validate the form automatically
    _shouldValidateOnSignIn = fieldValue => (this.props.type === 'email' &&
		(this.props.valid === false || (this.props.valid === null && this.isValid(fieldValue))))

	/////  ~~~~~~~~~~~ ---------- ~~~~~~~~~~~~  *************  ~~~~~~~~~~~ ---------- ~~~~~~~~~~~~  /////


	displayLabel = () => {
		return this.props.label ? <Label>{this.props.label}</Label> : null;
	};
	displayField = () => {
		switch (this.props.type) {
			case 'text':
			case 'color':
			case 'date':
			case 'datetime-local':
			case 'email':
			case 'password':
			case 'month':
			case 'number':
			case 'range':
			case 'search':
			case 'tel':
			case 'time':
			case 'url':
			case 'week':
				return <TextField type={this.props.type} name={this.props.name} placeholder={this.props.placeholder} readOnly={this.props.readOnly} value={this.props.value} defaultValue={this.props.defaultValue} handleFocus={this.handleFocus} handleBlur={this.handleBlur} handleKeyDown={this.handleKeyDown} handleChange={this.handleChange} disabled={this.props.disabled} />;
			case 'textarea':
				return <TextAreaField name={this.props.name} readOnly={this.props.readOnly} defaultValue={this.props.defaultValue} handleFocus={this.handleFocus} handleBlur={this.handleBlur} handleKeyDown={this.handleKeyDown} handleChange={this.handleChange} disabled={this.props.disabled} />;
			case 'select':
				return <SelectField name={this.props.name} readOnly={this.props.readOnly} value={this.props.value} options={this.props.options} dummyOptionText={this.props.dummyOptionText} handleFocus={this.handleFocus} handleBlur={this.handleBlur} handleChange={this.handleChange} disabled={this.props.disabled} />;
			case 'checkbox':
				return <CheckboxField name={this.props.name} checked={this.props.checked} handleChange={this.handleChange} disabled={this.props.disabled} />;
			case 'radio':
				return <RadioField name={this.props.name} checked={this.props.checked} handleChange={this.handleChange} disabled={this.props.disabled} />;
			default:
				return null;
		}
	};
	displayIcon = () => {
		return this.props.icon ? <span className={`icon`}>{this.props.icon}</span> : null;
	};
	displayInfo = () => {
		return this.props.info ? <Info>{this.props.info}</Info> : null;
	};
	displayErrorMessage = () => {
		return !this.props.required ? null : this.props.valid === false ? <ErrorMessage>{this.getErrorMessage()}</ErrorMessage> : null;
	};
	render() {
		return (
			<div className={`field ${generateClasses(this.props, ['icon'])} isActive-${this.state.active} disabled-${this.props.disabled} isValid-${this.props.valid} hasValue-${this.state.hasValue} ${this.props.classes.field}`}>
				{this.displayLabel()}
				{this.props.test}
	        	<div className={`formelement`}>
	        		{this.displayIcon()}
	        		<span className={`element`}>{this.displayField()}</span>
	        	</div>
	        	{this.displayInfo()}
	        	{this.displayErrorMessage()}
	        </div>
		);
	}
}



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(Field);